import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../hooks/useLanguage';
import { useGetUserProfileQuery } from '../../store/api/userApi';
import { useGetHideProductsStatusQuery } from '../../store/api/catalogApi';

// Navigation Icons
const DashboardIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const ProfileIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const BalanceIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const RechargeIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
    />
  </svg>
);

const TransactionsIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
    />
  </svg>
);

const CatalogIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
);

const PurchasesIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
    />
  </svg>
);

const AdminIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

const TemporaryNumbersIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const PermanentNumbersIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ProxiesIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
    />
  </svg>
);

const ApiDocsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SupportIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.12l-6.87 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
  </svg>
);

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navigationItems = [
  { path: '/dashboard', icon: DashboardIcon, translationKey: 'dashboard' },
  { path: '/catalog', icon: CatalogIcon, translationKey: 'catalog' },
  { path: '/purchases', icon: PurchasesIcon, translationKey: 'purchases' },
  { path: '/temporary-numbers', icon: TemporaryNumbersIcon, translationKey: 'temporaryNumbers' },
  { path: '/permanent-numbers', icon: PermanentNumbersIcon, translationKey: 'permanentNumbers' },
  { path: '/proxies', icon: ProxiesIcon, translationKey: 'proxies' },
  { path: '/profile', icon: ProfileIcon, translationKey: 'profile' },
  { path: '/balance', icon: BalanceIcon, translationKey: 'balance' },
  { path: '/recharge', icon: RechargeIcon, translationKey: 'recharge' },
  { path: '/transactions', icon: TransactionsIcon, translationKey: 'transactions' },
  { path: '/support', icon: SupportIcon, translationKey: 'support' },
  { path: '/api-docs.html', icon: ApiDocsIcon, translationKey: 'apiDocs', external: true },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // Check if user is admin
  const { data: userData } = useGetUserProfileQuery();

  // Check if products are hidden from website
  const { data: hideProductsData } = useGetHideProductsStatusQuery();
  const isProductsHidden = hideProductsData?.data?.hideProducts?.website === true;

  // Filter navigation items based on visibility settings
  const visibleNavItems = navigationItems.filter(
    (item) => !(item.path === '/catalog' && isProductsHidden)
  );
  const isAdmin = userData?.data.user.isAdmin || false;

  // على الموبايل: خلفية صلبة (أبيض في الوضع الفاتح، كحلي في الداكن) حتى تظهر كل الروابط بما فيها لوحة القيادة
  const mobileSolidBg = !isDesktop
    ? (theme === 'light' ? { backgroundColor: '#ffffff' } : { backgroundColor: '#0E4A4A' })
    : undefined;

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Overlay for mobile/tablet only - NOT for desktop */}
      {isOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - على الموبايل خلفية صلبة حتى تظهر كل العناصر بما فيها زر لوحة القيادة للأدمن */}
      <aside
        className={`app-sidebar fixed top-16 lg:top-20 h-[calc(100vh-4rem)] lg:h-[calc(100vh-5rem)] w-64 z-40 transform transition-all duration-300 ease-out ${
          isRTL ? 'right-0 border-l' : 'left-0 border-r'
        } ${
          isOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'
        } ${
          !isOpen && !isDesktop ? 'pointer-events-none opacity-0' : 'opacity-100'
        } ${
          isDesktop
            ? isScrolled
              ? 'bg-brand-primary/30 backdrop-blur-lg backdrop-saturate-150 border-brand-primary/40'
              : 'bg-transparent backdrop-blur-0 backdrop-saturate-100 border-transparent'
            : 'border-neutral-dark/40 shadow-2xl'
        }`}
        style={mobileSolidBg}
        role="navigation"
        aria-label="Main navigation"
        {...((!isOpen && !isDesktop) && { inert: 'true' })}
      >
        <nav className={`p-4 h-full overflow-y-auto ${!isDesktop && theme === 'light' ? 'text-neutral-dark' : ''}`}>
          <ul className="space-y-2">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path ||
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              const linkClass = !isDesktop && theme === 'light'
                ? isActive
                  ? `bg-brand-accent/20 text-brand-accent ${isRTL ? 'border-l-2' : 'border-r-2'} border-brand-accent`
                  : 'text-neutral-dark hover:bg-neutral-dark/20 hover:text-brand-accent'
                : isActive
                  ? `bg-brand-accent/20 text-brand-accent ${isRTL ? 'border-l-2' : 'border-r-2'} border-brand-accent`
                  : 'text-neutral-light hover:bg-brand-primary/20 hover:text-brand-accent';

              return (
                <li key={item.path}>
                  {'external' in item && item.external ? (
                    <a
                      href={`${item.path}?lang=${i18n.language.split('-')[0]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        if (!isDesktop) onClose();
                      }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body transition-all duration-200 ${linkClass}`}
                    >
                      <Icon />
                      <span>{t(`navigation.${item.translationKey}`)}</span>
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={() => {
                        if (!isDesktop) onClose();
                      }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body transition-all duration-200 ${linkClass}`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon />
                      <span>{t(`navigation.${item.translationKey}`)}</span>
                    </Link>
                  )}
                </li>
              );
            })}

            {/* لوحة القيادة (Admin) - تظهر للأدمن كزر كامل مع أيقونة ونص */}
            {isAdmin && (
              <>
                <li className="pt-4 mt-4 border-t border-neutral-dark">
                  <div className="px-4 py-2">
                    <span className={`text-xs font-body uppercase tracking-wider ${!isDesktop && theme === 'light' ? 'text-neutral-medium' : 'text-neutral-medium'}`}>
                      {t('navigation.admin')}
                    </span>
                  </div>
                </li>
                <li>
                  <Link
                    to="/admin/dashboard"
                    onClick={() => {
                      if (!isDesktop) onClose();
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body transition-all duration-200 w-full ${
                      !isDesktop && theme === 'light'
                        ? location.pathname.startsWith('/admin')
                          ? `bg-brand-accent/20 text-brand-accent ${isRTL ? 'border-l-2' : 'border-r-2'} border-brand-accent`
                          : 'text-neutral-dark hover:bg-neutral-dark/20 hover:text-brand-accent'
                        : location.pathname.startsWith('/admin')
                          ? `bg-brand-accent/20 text-brand-accent ${isRTL ? 'border-l-2' : 'border-r-2'} border-brand-accent`
                          : 'text-neutral-light hover:bg-brand-primary/20 hover:text-brand-accent'
                    }`}
                    aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
                  >
                    <AdminIcon />
                    <span>{t('navigation.adminDashboard')}</span>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>
      </aside>
    </>
  );
}

